import { useState } from "react";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import bitmapSample from "../../../ConceptOne/src/data/samples/from-bitmap.json";
import efhSample from "../../../ConceptOne/src/data/samples/efh-1.json";
import { C1_DOCUMENT_LOGO } from "../../../ConceptOne/src/C1_Const";
import {
  C1_ProjectForm,
  type C1_ProjectFormValue,
} from "../../../ConceptOne/src/app/C1_ProjectForm";
import { emptyModel, modelFromSample } from "../../../ConceptOne/src/lib/C1_initialModel";
import BaseDialog from "../../modelDialogs/BaseDialog";
import { itx } from "../../translationItx/itx";
import { EventKey } from "../../eventLogger/EventKeys";
import { C1_PdfImportButton } from "../../../ConceptOne/src/components/C1_PdfImportButton";
import "../../../ConceptOne/src/C1_styles.scss";

const samples = [
  {
    label: "Grundriss aus Bitmap", // no itx
    data: bitmapSample,
  },
  {
    label: "Einfamilienhaus 1", // no itx
    data: efhSample,
  },
];

const initialForm: C1_ProjectFormValue = {
  projectName: "",
  sampleIndex: -1,
  houseType: "detachedHouse",
  buildingWidth: 12,
  buildingLength: 12,
  floorDistance: 3,
  equipmentStandard: 4,
};

function ConceptOneCreateProjectDialog({ openProject }: { openProject: () => void }) {
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState("");

  const createProject = async () => {
    const name = form.projectName.trim();
    console.info(
      "[C1 Create] Projektanlage gestartet", // no itx
      {
        name,
        sampleIndex: form.sampleIndex,
        currentPath: window.location.pathname,
      },
    );
    state.eventLogger.addEvent(EventKey.ConceptOneProjectCreationSubmitted, {
      source: "estadaProjectSelection", // no itx
      projectName: name,
    });
    if (!name) {
      console.warn("[C1 Create] Validierung fehlgeschlagen: Projektname fehlt");
      setError("Bitte einen Projektnamen eingeben.");
      return false;
    }
    if (
      !Number.isFinite(form.buildingWidth) ||
      !Number.isFinite(form.buildingLength) ||
      !Number.isFinite(form.floorDistance) ||
      form.buildingWidth <= 0 ||
      form.buildingLength <= 0 ||
      form.floorDistance <= 0
    ) {
      console.warn("[C1 Create] Validierung fehlgeschlagen: ungültige Gebäudemaße", {
        buildingWidth: form.buildingWidth,
        buildingLength: form.buildingLength,
        floorDistance: form.floorDistance,
      });
      setError("Breite, Länge und Etagenabstand müssen größer als 0 sein.");
      return false;
    }

    try {
      const dimensions = {
        houseType: form.houseType,
        width: Math.round(form.buildingWidth * 10) / 10,
        length: Math.round(form.buildingLength * 10) / 10,
        floorDistance: Math.round(form.floorDistance * 10) / 10,
        equipmentStandard: form.equipmentStandard,
      };
      console.info(
        "[C1 Create] Erzeuge Projektmodell", // no itx
        dimensions,
      );
      const c1State =
        form.sampleIndex === -1
          ? emptyModel(name, dimensions)
          : modelFromSample(samples[form.sampleIndex].data, name, dimensions);
      console.info(
        "[C1 Create] Projektmodell erzeugt", // no itx
        {
          projectId: c1State.project.id,
          projectName: c1State.project.projectName,
          floorCount: c1State.project.floors.length,
          currentFloorId: c1State.currentFloorId.value,
          metadataId: state.projectMetadata.id,
        },
      );
      state.eventLogger.addEvent(EventKey.ConceptOneProjectCreated, {
        source: "estadaProjectSelection", // no itx
        projectId: c1State.project.id,
        projectName: name,
      });
      state.eventLogger.flush();
      console.info(
        "[C1 Create] Navigation zum C1-Editor wird ausgelöst", // no itx
        { target: "/concept-one" }, // no itx
      );
      openProject();
      console.info("[C1 Create] Navigationsaufruf abgeschlossen", { currentPath: window.location.pathname });
      return true;
    } catch (reason) {
      console.error("[C1 Create] Projektanlage fehlgeschlagen", reason);
      setError(reason instanceof Error ? reason.message : "Projekt konnte nicht angelegt werden.");
      return false;
    }
  };

  return (
    <BaseDialog
      dialogId="concept-one-create-project"
      className="conceptOneCreateProjectDialog"
      title={itx("ConceptOneProjectCard.neuesProjektErstellen", "Neues Projekt erstellen")}
      okCaption={itx("ConceptOneProjectCard.projektErstellen", "Projekt erstellen")}
      abortCaption={itx("ConceptOneProjectCard.abbrechen", "Abbrechen")}
      onOkAsync={createProject}
      enterAsOk
      noMinHeight
      bodyNoMinWidth
    >
      <div className="C1 conceptOneCreateProjectForm">
        <C1_ProjectForm
          value={form}
          onChange={(value) => {
            setForm(value);
            setError("");
          }}
          samples={samples}
          idPrefix="estada-create-c1-project"
          showTemplate={false}
          showEquipment={false}
        />
        {error && (
          <p className="conceptOneCreateProjectError" role="alert">
            {error}
          </p>
        )}
      </div>
    </BaseDialog>
  );
}

export default function ConceptOneProjectCard() {
  const navigate = useNavigate();

  const openCreateDialog = () => {
    console.info("[C1 Create] Dialog zur Projektanlage wird geöffnet");
    state.eventLogger.addEvent(EventKey.ConceptOneEntryButtonClicked, { button: "createProject" });
    state.dialogManager.openDialog(
      <ConceptOneCreateProjectDialog
        openProject={() => {
          console.info("[C1 Create] React-Router-Navigation", { from: window.location.pathname, to: "/concept-one" });
          navigate("/concept-one");
        }}
      />,
    );
  };

  return (
    <div
      className="projectSelectionCard conceptOneProjectCard"
      style={{ backgroundColor: "var(--primary-color-light)" }}
    >
      <CardContent className="conceptOneProjectCardContent">
        <img className="conceptOneProjectCardLogo" src={C1_DOCUMENT_LOGO} alt="" aria-hidden="true" />
        <div className="conceptOneProjectCardHeading">
          <Typography variant="h5" component="div">Concept One</Typography>
          <Typography variant="body2" component="p" className="conceptOneProjectCardSubtitle">
            {itx("homePage.newConceptOneProjectSubtitle", "Neues Projekt anlegen")}
          </Typography>
        </div>
      </CardContent>
      <CardActions className="conceptOneProjectCardActions">
        <button type="button" className="button" onClick={openCreateDialog}>
          {itx("homePage.newConceptOneProjectButton", "Neues Projekt")}
        </button>
        <div className="C1">
          <C1_PdfImportButton
            createProject={(file, suggestedProjectName) => {
              const projectName = suggestedProjectName ?? file.name.replace(/\.pdf$/i, "");
              emptyModel(projectName, undefined, { forPdfImport: true });
              state.eventLogger.addEvent(EventKey.ConceptOneProjectCreated, {
                source: "estadaProjectSelection", projectId: state.projectMetadata.id, projectName,
              });
            }}
            onImported={() => navigate("/concept-one")}
          />
        </div>
      </CardActions>
    </div>
  );
}
